import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faTimesCircle as farTimesCircle } from "@fortawesome/free-solid-svg-icons";
import styled, { css } from "styled-components";
import hexToRGB from "../helpers/hexToRGB";
import { getTimeWindow } from "../helpers/location";

library.add(farTimesCircle);

const IconContainer = styled.div`
  position: absolute;
  top: 5px;
  cursor: pointer;
`;

const IconContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 7px;
  background-color: ${({ color }) =>
    hexToRGB(color, 0.8) || hexToRGB("gray", 0.8)};
  border: 1px solid ${({ color }) => color || "gray"};
  text-align: center;
  border-radius: 50%;
  color: white;
`;

const LocationIcon = styled.div`
  position: absolute;
  text-align: center;
`;

const LocationNumber = styled.span`
  display: block;
  width: 16px;
  text-align: center;
  font-family: Lato;
  font-size: 9px;
  line-height: 16px;
  color: ${({ color }) => color || "#C4C4C4"};
  background: #fff;
  border-radius: ${({ type }) => (type === "dropoff" ? 0 : "50%")};
  border: 2px solid ${({ color }) => color || "#C4C4C4"};
`;

const Box = styled.div`
  position: relative;
  margin-left: 1.857em;
  margin-top: ${({ marginTop }) => marginTop || "0"};
  margin-bottom: ${({ marginBottom }) => marginBottom || "0"};
  cursor: pointer;
`;

const BoxContent = styled.div`
  margin-left: 2px;
  padding: 5px 10px;
  ${({ selected }) =>
    selected &&
    css`
      border-radius: 2px;
      background-color: ${({ color }) =>
        hexToRGB(color, 0.2) || hexToRGB("#C4C4C4", 0.2)};
    `}
`;

const StopsCount = styled.span`
  float: right;
  display: inline-block;
  background: ${({ color }) => color || "#C4C4C4"};
  border-radius: 10px;
  font-size: 12px;
  color: #ffffff;
  text-align: center;
  min-width: 24px;
  margin-left: 15px;
`;

const BoxContainer = styled.div`
  margin-left: 0.714em;
  padding-left: ${({ paddingLeft }) => paddingLeft || "9px"};
  ${({ borderLeftColor }) =>
    borderLeftColor &&
    css`
      border-left: 0.071em dashed ${borderLeftColor};
    `}
`;

const Location = styled.div`
  ${({ isSubContent }) =>
    isSubContent &&
    css`
      margin-left: 30px;
    `}
  .address {
    margin: 0.271em 0 0 0;
    font-size: 0.714em;
    font-family: Lato;
    color: #56666c;
  }
`;

const Badge = styled.span`
  padding: 0.286em 0.5em;
  border: 0.071em solid ${({ color }) => color || "#C4C4C4"};
  border-radius: 0.143em;
  font-size: 0.643em;
  font-family: Lato;
  font-weight: 700;
  cursor: pointer;
  color: ${({ color }) => color || "#C4C4C4"};
  background-color: ${({ color }) =>
    hexToRGB(color, 0.2) || hexToRGB("#C4C4C4", 0.2)};
`;

const Title = styled.p`
  font-size: 0.714em;
  font-family: Lato;
  font-weight: 700;
  color: #56666c;
  margin: 0;
`;

// {false && <FontAwesomeIcon icon={["fa", "truck"]} />}
// {false && <FontAwesomeIcon icon={["fa", "flag"]} />}
// {false && <FontAwesomeIcon icon={["far", "circle"]} />}

// 1.857+0.714em
class Fleet extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isToggled: false,
      selectedLocationIndex: 0,
    };
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  getDepotIcon(location) {
    if (location.vehicleId === "unserved" && location.isStartingLocation) {
      return (
        <IconContainer>
          <IconContent color={`#${location.color}`}>
            <FontAwesomeIcon icon={"times-circle"} style={{ fontSize: 12 }} />
          </IconContent>
        </IconContainer>
      );
    }

    if (location.isStartingLocation) {
      return (
        <IconContainer>
          <IconContent color={`#${location.color}`}>
            <FontAwesomeIcon icon={"truck"} flip="horizontal" />
          </IconContent>
        </IconContainer>
      );
    }

    if (location.isLastLocation) {
      return (
        <IconContainer>
          <IconContent color={`#${location.color}`}>
            <FontAwesomeIcon icon={"flag"} />
          </IconContent>
        </IconContainer>
      );
    }

    return null;
  }

  getBorderLeftColor(location) {
    if (!location.isStartingLocation && !location.isLastLocation) {
      return `#${location.color}`;
    }
    return undefined;
  }

  getLocations() {
    const { locations } = this.props;
    if (this.state.isToggled) return locations;

    return locations.slice(0, 1);
  }

  getLocationsCount() {
    const locations = this.props.locations || [];

    return _.filter(locations, { isVisit: true }).length;
  }

  getLocationIcon(location) {
    if (location.isStartingLocation || location.isLastLocation) {
      return null;
    }

    return (
      <LocationIcon color={`#${location.color}`} type={location.type}>
        <LocationNumber
          className="number"
          color={`#${location.color}`}
          type={location.vType}
        >
          {location.number}
        </LocationNumber>
      </LocationIcon>
    );
  }

  getLocationTitle(location) {
    if (location.isStartingLocation) {
      return (
        <Badge
          color={`#${location.color}`}
          onClick={this.selectLocation(location)}
        >
          {location.vehicleId !== "unserved"
            ? location.vehicleId
            : "Unserved Stops"}
        </Badge>
      );
    }

    if (location.isLastLocation) {
      return <Title>End Location</Title>;
    }
    const type = location.type ? `(${location.type})` : "";

    return (
      <Title>
        {location.location_id} {type}{" "}
        <span style={{ float: "right" }}>{getTimeWindow(location)}</span>
      </Title>
    );
  }

  render() {
    const locations = this.getLocations();

    return locations.map((location, i) => (
      <Box
        key={i}
        marginTop={location.isLastLocation ? "7px" : undefined}
        marginBottom={location.isStartingLocation ? "5px" : undefined}
        onClick={this.toggleLocations(location)}
      >
        {this.getDepotIcon(location)}
        <BoxContainer
          borderLeftColor={this.getBorderLeftColor(location)}
          onClick={this.selectLocation(location, i)}
        >
          <BoxContent
            color={`#${location.color}`}
            selected={this.checkSelected(location)}
          >
            {location.isStartingLocation && (
              <StopsCount color={`#${location.color}`}>
                {this.getLocationsCount()}
              </StopsCount>
            )}
            {this.getLocationIcon(location)}
            <Location
              isSubContent={
                !location.isStartingLocation && !location.isLastLocation
              }
            >
              {this.getLocationTitle(location)}
              <p className="address">{location.name}</p>
              {location.address && (
                <p className="address">{location.address}</p>
              )}
            </Location>
          </BoxContent>
        </BoxContainer>
      </Box>
    ));
  }

  checkSelected(location) {
    const { currentLocation } = this.props;
    return (
      currentLocation &&
      currentLocation.lat === location.lat &&
      currentLocation.lng === location.lng &&
      currentLocation.location_id === location.location_id &&
      currentLocation.type === location.type &&
      currentLocation.number === location.number &&
      currentLocation.vehicleId === location.vehicleId
    );
  }

  selectLocation = (location, index) => {
    return () => {
      if (location.lat) {
        if (index !== undefined) {
          this.setState({ selectedLocationIndex: index });
        }
        this.props.updateLocationState(location);
      }
    };
  };

  toggleLocations = (location) => {
    return function toggle() {
      if (location.isStartingLocation) {
        this.setState({ isToggled: !this.state.isToggled });
      }
    }.bind(this);
  };

  componentDidMount() {
    window.addEventListener("keydown", this.handleKeyDown);
  }

  handleKeyDown(event) {
    const { selectedLocationIndex } = this.state;
    const locations = this.getLocations();
    const currentVehicleId = locations[selectedLocationIndex]?.vehicleId;

    if (event.key === "ArrowDown") {
      if (selectedLocationIndex < locations.length - 1) {
        const nextLocation = locations[selectedLocationIndex + 1];
        if (nextLocation.vehicleId === currentVehicleId) {
          this.setState(
            { selectedLocationIndex: selectedLocationIndex + 1 },
            () => {
              this.selectLocation(
                locations[this.state.selectedLocationIndex]
              )();
            }
          );
        }
      }
    } else if (event.key === "ArrowUp") {
      if (selectedLocationIndex > 0) {
        const prevLocation = locations[selectedLocationIndex - 1];
        if (prevLocation.vehicleId === currentVehicleId) {
          this.setState(
            { selectedLocationIndex: selectedLocationIndex - 1 },
            () => {
              this.selectLocation(
                locations[this.state.selectedLocationIndex]
              )();
            }
          );
        }
      }
    }
  }
}

Fleet.propTypes = {
  locations: PropTypes.array.isRequired,
  updateLocationState: PropTypes.func.isRequired,
  currentLocation: PropTypes.object,
};

export default Fleet;
